import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Search, X, ChevronLeft, ChevronRight, Trash2 } from "lucide-react";
import { UserData } from "../../types/user-data.type";
import useApi from "../../hooks/api/useApi";
import { User, IPagination } from "../../types/user.type";

export default function AdminUsersList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [query, setQuery] = useState(searchParams.get("q") ?? "");
  const [items, setItems] = useState<User[]>([]);
  const [pagination, setPagination] = useState<IPagination | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const userData: UserData = JSON.parse(localStorage.getItem("user") ?? "");
  const { request } = useApi();

  const authHeaders = {
    "Content-Type": "application/json",
    Authorization: "Bearer " + userData.authToken,
  };

  const fetchUsers = useCallback(async (q: string, page: number) => {
    setIsLoading(true);
    setSearched(true);
    try {
      let qs = "?page=" + page + "&";
      if (q) qs += "search=" + q + "&";
      await request(
        "/users" + qs,
        { method: "Get", headers: authHeaders },
        (result) => {
          setItems(result.payload.items);
          setPagination(result.payload.pagination);
        }
      );
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers(searchParams.get("q") ?? "", currentPage);
  }, [searchParams, currentPage, fetchUsers]);

  const handleSearch = () => {
    const params: Record<string, string> = { page: "1" };
    if (query.trim()) params.q = query.trim();
    setSearchParams(params);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") handleSearch();
  };

  const handleClear = () => {
    setQuery("");
    setSearchParams({ page: "1" });
  };

  const handlePageChange = (page: number) => {
    const params: Record<string, string> = { page: String(page) };
    if (query.trim()) params.q = query.trim();
    setSearchParams(params);
    setCurrentPage(page);
  };

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    try {
      await request(
        `/users/${id}`,
        { method: "Delete", headers: authHeaders },
        () => setItems((prev) => prev.filter((u) => u.id !== id))
      );
    } catch (err) {
      console.error(err);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="min-h-screen text-gray-800 font-sans transition-colors duration-500">
      <div className="mx-auto min-h-screen shadow-2xl relative overflow-hidden transition-colors duration-500">
        <main className="h-full">
          <div className="p-6 space-y-6">
            <h1 className="text-2xl font-semibold text-gray-800">کاربران</h1><div className="flex gap-2">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="جستجو در کاربران..."
                  className="w-full pr-9 pl-9 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {query && (
                  <button
                    onClick={handleClear}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              <button
                onClick={handleSearch}
                className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
              >
                جستجو
              </button>
              <button
                onClick={() => navigate("/admin/users/new")}
                className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
              >
                ایجاد کاربر جدید
              </button>
            </div><div className="border border-gray-200 rounded-lg overflow-hidden">
              <table className="w-full text-sm text-right">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="px-4 py-3 font-medium">نام</th>
                    <th className="px-4 py-3 font-medium">نام کاربری</th>
                    <th className="px-4 py-3 font-medium">نقش‌ها</th>
                    <th className="px-4 py-3 font-medium">وضعیت</th>
                    <th className="px-4 py-3 font-medium"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {isLoading ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-10 text-center text-gray-400">
                        در حال بارگذاری...
                      </td>
                    </tr>
                  ) : items.length === 0 && searched ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-10 text-center text-gray-400">
                        کاربری یافت نشد.
                      </td>
                    </tr>
                  ) : (
                    items.map((item) => (
                      <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3 font-medium text-gray-800">
                          {item.firstName} {item.lastName}
                        </td>
                        <td className="px-4 py-3 text-gray-500">{item.username}</td>
                        <td className="px-4 py-3 text-gray-500">
                          {item.roles.join("، ")}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                              item.active
                                ? "bg-green-100 text-green-700"
                                : "bg-red-100 text-red-600"
                            }`}
                          >
                            {item.active ? "فعال" : "غیرفعال"}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-2 justify-end">
                            <button
                              onClick={() => navigate(`/admin/users/${item.id}/edit`)}
                              className="text-gray-600 hover:text-gray-800 text-sm"
                            >
                              ویرایش
                            </button>
                            <button
                              onClick={() => handleDelete(item.id)}
                              disabled={deletingId === item.id}
                              className="text-red-500 hover:text-red-700 disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {pagination && pagination.totalPages > 1 && (
              <div className="flex items-center justify-between text-sm text-gray-600">
                <span>
                  {pagination.totalItems} مورد — صفحه {pagination.currentPage} از{" "}
                  {pagination.totalPages}
                </span>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage <= 1}
                    className="p-1.5 rounded hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>

                  {Array.from({ length: pagination.totalPages }, (_, i) => i + 1)
                    .filter(
                      (p) =>
                        p === 1 ||
                        p === pagination.totalPages ||
                        Math.abs(p - currentPage) <= 1
                    )
                    .reduce<(number | "...")[]>((acc, p, idx, arr) => {
                      if (idx > 0 && p - (arr[idx - 1] as number) > 1) acc.push("...");
                      acc.push(p);
                      return acc;
                    }, [])
                    .map((p, idx) =>
                      p === "..." ? (
                        <span key={`ellipsis-${idx}`} className="px-2">...</span>
                      ) : (
                        <button
                          key={p}
                          onClick={() => handlePageChange(p as number)}
                          className={`w-8 h-8 rounded text-sm ${
                            p === currentPage ? "bg-blue-600 text-white" : "hover:bg-gray-100"
                          }`}
                        >
                          {p}
                        </button>
                      )
                    )}<button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage >= pagination.totalPages}
                    className="p-1.5 rounded hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
